// "Waar dit op rust" onder een chatantwoord (ADR trends-bundelen, eindstand
// 23-10: "antwoord met nummers + 'Waar dit op rust' per trend + Waarom nu-strip +
// 'overwogen, niet gebruikt'"). Alleen met de schakelaar aan (schakelaar.ts).
//
// Ronde 3: het antwoord draagt zijn eigen kader (`trendlaag` in het chatantwoord,
// sof/readapi/chat_trendlaag.py). De nummers [n] in de tekst zijn de nummers van
// de ontwikkelingen hieronder: één nummering, gezet door de service, nooit door
// het scherm geraden. Zonder kader (een ouder bewaard gesprek) valt dit terug op
// /api/trendlaag/rust met de trendkaarten van het antwoord, zoals vóór ronde 3.
//
// Editie 15 (owner 26-10: C14 = b, N14 = a): de vorm van het oude blok "Trends
// bij dit antwoord · n" (witte lijstkaart, stip, bloklabel) met één regel per
// trend en de nummers als chipjes; uitklappen toont de ontwikkelingen met datum
// en bronnen; de overwogen trend staat er grijs bij. Volgens CH-A bovenaan het
// antwoord de telzin met "✓ geteld" (TelZin), klikbare nummers in de tekst
// (NummerChip), het blok ónder de antwoordbel en één aparte Waarom nu-strip met
// het getal groot.

import { type ReactNode, useEffect, useRef, useState } from "react";

import type { ChatResponse, KaartLanding } from "../../contracts";
import { api, type TrendlaagRust } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import { color, radius, weight } from "../../components/chatTokens";
import { Bloklabel, KV, Lijst, Stip } from "../../components/ChatBlokken";
import { beweging } from "./woorden";

/** Het kader van één antwoord (api/chat.schema.json#/$defs/trendlaagKader). */
export type Kader = NonNullable<ChatResponse["trendlaag"]>;
type RustTrend = Kader["rust"]["trends"][number];
type RustOntwikkeling = RustTrend["ontwikkelingen"][number];
type Overwogen = Kader["rust"]["overwogen"][number];

const nf = new Intl.NumberFormat("nl-NL");
const MAANDEN = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

/** Regel W1: het getal uit de waarom-nu-zin, groot in de strip. De service kiest
 *  het (waaromNuGetal); zonder dat veld (een ouder kader) kiest dit zelf: een
 *  getal met eenheid gaat voor een kaal getal, een jaartal alleen als er niets
 *  anders is ("eind 2027 … slechts 12%" → "12%"). */
export function waaromNuStrip(
  zin: string | null | undefined,
  getal?: string | null,
): { getal: string | null; zin: string } | null {
  const z = (zin ?? "").trim();
  if (!z) return null;
  if (getal !== undefined) return { getal: getal || null, zin: z };
  // Een cijfer ín een woord (NIS2, 5G) is geen getal.
  const treffers = [...z.matchAll(/(?<![\w.,])(?:€\s?)?\d+(?:[.,]\d+)*(?:\s?(?:%|procent|miljoen|miljard))?(?!\w)/g)].map((m) =>
    m[0].replace(/\s+/g, " "),
  );
  const rang = (t: string) => (/%|procent|€|miljoen|miljard/.test(t) ? 0 : /^(?:19|20)\d\d$/.test(t) ? 2 : 1);
  const beste = [...treffers].sort((a, b) => rang(a) - rang(b))[0];
  return { getal: beste ? beste.replace(/\s?procent/, "%") : null, zin: z };
}

/** Het oude antwoord van /trendlaag/rust in de vorm van het kader, met de
 *  ontwikkelingen doorlopend genummerd (zonder nummers in de tekst). */
export function kaderUitRust(r: TrendlaagRust): Kader {
  let nr = 0;
  const trends: RustTrend[] = r.trends.map((t) => {
    const ontwikkelingen = t.ontwikkelingen.map((o) => ({ ...o, nr: ++nr, bevindingen: [] as string[] }));
    return { ...t, ontwikkelingen, nummers: ontwikkelingen.map((o) => o.nr) };
  });
  const n = trends.reduce((s, t) => s + t.ontwikkelingen.length, 0);
  const b = trends.reduce((s, t) => s + t.bevindingen.length, 0);
  return {
    telling: { bevindingen: b, ontwikkelingen: n, trends: trends.length },
    rust: { trends, overwogen: [], nietIngedeeld: r.nietIngedeeld },
  };
}

const meervoud = (n: number, een: string, meer: string) => `${nf.format(n)} ${n === 1 ? een : meer}`;

/** "sinds jul 2025" (oudste bewijs) of "vanaf 10 december 2027" (een datum die
 *  nog moet komen), zoals de regels in CH-A. */
export function datumRegel(
  o: Pick<RustOntwikkeling, "datum" | "datumTekst" | "eerste">,
  vandaag = new Date(),
): string | null {
  const nu = vandaag.toISOString().slice(0, 10);
  if (o.datum && o.datum > nu) return `vanaf ${o.datumTekst || o.datum.slice(0, 4)}`;
  const e = o.eerste ?? "";
  if (/^\d{4}-\d\d/.test(e)) return `sinds ${MAANDEN[Number(e.slice(5, 7)) - 1]} ${e.slice(0, 4)}`;
  return null;
}

/* ------------------------------------------------------------------------- *
 * In de antwoordbel: de telzin bovenaan en de nummers als chipjes.
 * ------------------------------------------------------------------------- */

/** CH-A: de eerste regel van het antwoord, "Gebaseerd op 5 ontwikkelingen uit 3
 *  trends ✓ geteld, met waarom-nu-bewijs uit 2 trends". De getallen komen uit
 *  het kader (door de service geteld), niet uit de tekst van het model. */
export function TelZin({ kader }: { kader: Kader }) {
  const trends = kader.rust.trends;
  if (trends.length === 0) return null;
  const ontw = trends.reduce((s, t) => s + t.ontwikkelingen.length, 0);
  const metNu = trends.filter((t) => (t.waaromNu ?? "").trim()).length;
  const kern =
    ontw > 0
      ? `${meervoud(ontw, "ontwikkeling", "ontwikkelingen")} uit ${meervoud(trends.length, "trend", "trends")}`
      : meervoud(trends.length, "trend", "trends");
  return (
    <div data-testid="telzin" style={{ marginBottom: 8 }}>
      Gebaseerd op <b style={{ fontWeight: weight.bold, color: color.ink }}>{kern}</b>
      <span
        title="Geteld in de trendlaag, niet door het taalmodel geschat"
        style={{
          display: "inline-flex", alignItems: "center", marginLeft: 6, fontSize: 9.5, fontWeight: weight.bold,
          letterSpacing: "0.04em", textTransform: "uppercase", color: color.accentGreen,
          background: `color-mix(in srgb, ${color.accentGreen} 12%, transparent)`,
          border: `1px solid color-mix(in srgb, ${color.accentGreen} 35%, transparent)`,
          borderRadius: radius.pill, padding: "1px 6px", verticalAlign: "middle", whiteSpace: "nowrap",
        }}
      >
        ✓ geteld
      </span>
      {metNu > 0 ? `, met waarom-nu-bewijs uit ${meervoud(metNu, "trend", "trends")}.` : "."}
    </div>
  );
}

/** Een nummer [n] in de antwoordtekst: een klein chipje dat naar zijn regel in
 *  "Waar dit op rust" wijst (klik = de trend klapt open, de regel licht op). */
export function NummerChip({ nr, onKlik }: { nr: number; onKlik?: (nr: number) => void }) {
  return (
    <button
      type="button"
      aria-label={`naar ontwikkeling ${nr}`}
      onClick={() => onKlik?.(nr)}
      style={{
        border: "none", boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center",
        minWidth: 16, height: 16, padding: "0 4px", margin: "0 1px 0 3px", borderRadius: 4, verticalAlign: "2px",
        font: "inherit", fontSize: 10.5, fontWeight: weight.bold, lineHeight: 1, fontVariantNumeric: "tabular-nums",
        color: color.accentPurple, background: `color-mix(in srgb, ${color.accentPurple} 12%, transparent)`,
        cursor: onKlik ? "pointer" : "default",
      }}
    >
      {nr}
    </button>
  );
}

/** [1] · [2][3] · [1, 3] in een stuk tekst — dezelfde vorm die de service leest
 *  (chat_trendlaag.NUMMER_RE); geen [[markers]] en geen markdownlinks. */
const NUMMER_IN_TEKST = /(?<!\[)\[(\d{1,2}(?:\s*,\s*\d{1,2})*)\](?![\](])/g;

/** Knipt een stuk tekst op de nummers: tekst gaat door `tekst` (daar krijgen
 *  getallen nog hun telbadge), een bekend nummer wordt een NummerChip. Een
 *  onbekend nummer blijft staan zoals het model het schreef — liever zichtbaar
 *  dan stil weg. */
export function metNummerChips(
  stuk: string,
  bekend: ReadonlySet<number>,
  onKlik: ((nr: number) => void) | undefined,
  tekst: (deel: string, k: string) => void,
  chip: (node: ReactNode) => void,
  sleutel: string,
) {
  let laatste = 0;
  for (const m of stuk.matchAll(NUMMER_IN_TEKST)) {
    const nrs = (m[1] ?? "").split(",").map((x) => Number(x.trim()));
    if (!nrs.every((n) => bekend.has(n))) continue;
    const begin = m.index ?? 0;
    // De spatie vóór het nummer valt weg: het chipje hangt aan het woord.
    tekst(stuk.slice(laatste, begin).replace(/[ \t]+$/, ""), `${sleutel}-t${laatste}`);
    for (const n of nrs) chip(<NummerChip key={`${sleutel}-n${begin}-${n}`} nr={n} onKlik={onKlik} />);
    laatste = begin + m[0].length;
  }
  tekst(stuk.slice(laatste), `${sleutel}-t${laatste}`);
}

/* ------------------------------------------------------------------------- *
 * Onder de antwoordbel: "Waar dit op rust", de Waarom nu-strip, de rest.
 * ------------------------------------------------------------------------- */

/** onTrend krijgt een bevinding-id van die trend: de trendkaart vertaalt hem via
 *  /trendlaag/van. onOntwikkeling opent de ontwikkelingenkaart (CH-A: een klik
 *  op een ontwikkeling is die ontwikkeling, niet haar trend). `actief` = het
 *  nummer waarop in de tekst geklikt is (`keer` telt mee, zodat een tweede klik
 *  op hetzelfde nummer opnieuw oplicht). */
type Props = {
  kader?: Kader | null;
  bevindingIds: string[];
  onTrend?: (bevindingId: string) => void;
  onOntwikkeling?: (id: string, titel: string) => void;
  actief?: { nr: number; keer: number } | null;
};

/** Een overwogen trend: de ontwikkelingen die de tools van haar vonden en die
 *  het antwoord niet gebruikte, onder haar naam. */
type OverwogenTrend = { sleutel: string; titel: string; bronnen: number | null; ontwikkelingen: Overwogen[] };

const overwogenSleutel = (o: Overwogen) => `ov:${o.trendId ?? o.trendTitel ?? ""}`;

export function WaarDitOpRust({ kader, bevindingIds, onTrend, onOntwikkeling, actief }: Props) {
  const sleutel = kader ? "" : bevindingIds.join(",");
  const r = useResource(() => (sleutel ? api.trendlaagRust(bevindingIds) : Promise.resolve(null)), [sleutel]);
  const d: Kader | null = kader ?? (r.data ? kaderUitRust(r.data) : null);
  // De eerste trend staat open (variant b); de rest na een klik.
  const eerste = d?.rust.trends[0]?.trendId;
  const [open, setOpen] = useState<Set<string>>(() => new Set(eerste ? [eerste] : []));
  const eersteGezien = useRef(Boolean(eerste));
  useEffect(() => {
    // Een terugval-kader komt later binnen: dan gaat de eerste trend alsnog open.
    if (eerste && !eersteGezien.current) {
      eersteGezien.current = true;
      setOpen(new Set([eerste]));
    }
  }, [eerste]);
  const actiefNr = actief?.nr;
  const actiefKeer = actief?.keer;
  useEffect(() => {
    // Klik op een nummer in de tekst: zijn trend gaat open.
    if (actiefNr === undefined || !d) return;
    const t = d.rust.trends.find((x) => x.ontwikkelingen.some((o) => o.nr === actiefNr));
    const ov = d.rust.overwogen.find((o) => o.nr === actiefNr);
    const doel = t?.trendId ?? (ov ? (d.rust.trends.some((x) => x.trendId === ov.trendId) ? ov.trendId! : overwogenSleutel(ov)) : null);
    if (doel) setOpen((s) => new Set(s).add(doel));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen op een nieuwe klik
  }, [actiefNr, actiefKeer]);

  if (!d) return null;
  const { trends, overwogen, nietIngedeeld } = d.rust;
  if (trends.length === 0 && overwogen.length === 0 && nietIngedeeld.length === 0) return null;

  const gebruikt = new Set(trends.map((t) => t.trendId));
  const overwogenBinnen = new Map<string, Overwogen[]>();
  const overwogenTrends = new Map<string, OverwogenTrend>();
  for (const o of overwogen) {
    if (o.trendId && gebruikt.has(o.trendId)) {
      overwogenBinnen.set(o.trendId, [...(overwogenBinnen.get(o.trendId) ?? []), o]);
      continue;
    }
    const k = overwogenSleutel(o);
    const t = overwogenTrends.get(k) ?? {
      sleutel: k, titel: o.trendTitel || "Zonder trend", bronnen: o.trendBronnen ?? null, ontwikkelingen: [],
    };
    t.ontwikkelingen.push(o);
    overwogenTrends.set(k, t);
  }
  const wissel = (k: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const metNu = trends.filter((t) => (t.waaromNu ?? "").trim());
  const aantalRegels = trends.length + overwogenTrends.size;
  const oplicht = (nr: number) => (actiefNr === nr && actiefKeer !== undefined ? actiefKeer : null);

  return (
    <div style={{ margin: "12px 0 4px" }}>
      {aantalRegels > 0 && (
        <div aria-label="Waar dit op rust">
          <Bloklabel tekst={`Waar dit op rust · ${meervoud(trends.length, "trend", "trends")}`} />
          <Lijst>
            {trends.map((t, i) => (
              <TrendRegel
                key={t.trendId}
                titel={t.titel}
                nummers={t.ontwikkelingen.map((o) => o.nr)}
                bronnen={t.bronnen ?? null}
                opkomend={Boolean(t.opkomend)}
                wet={Boolean(t.wet)}
                beweging={t.beweging ?? null}
                open={open.has(t.trendId)}
                onWissel={() => wissel(t.trendId)}
                laatste={i === aantalRegels - 1}
              >
                <TrendVoet
                  delen={[t.onderwerp, bronSoortTekst(t.bronnen ?? null, t.bronSoorten)]}
                  onOpen={onTrend ? () => onTrend(t.bevindingen[0] ?? t.trendId) : undefined}
                />
                {t.ontwikkelingen.map((o) => (
                  <OntwikkelingRegel
                    key={o.id}
                    o={o}
                    actief={oplicht(o.nr)}
                    onKlik={onOntwikkeling ? () => onOntwikkeling(o.id, o.titel) : undefined}
                  />
                ))}
                {(overwogenBinnen.get(t.trendId) ?? []).map((o) => (
                  <OverwogenRegel
                    key={o.id}
                    o={o}
                    actief={oplicht(o.nr)}
                    onKlik={onOntwikkeling ? () => onOntwikkeling(o.id, o.titel) : undefined}
                  />
                ))}
              </TrendRegel>
            ))}
            {[...overwogenTrends.values()].map((t, i) => (
              <TrendRegel
                key={t.sleutel}
                titel={t.titel}
                nummers={t.ontwikkelingen.map((o) => o.nr)}
                bronnen={t.bronnen}
                overwogen
                open={open.has(t.sleutel)}
                onWissel={() => wissel(t.sleutel)}
                laatste={trends.length + i === aantalRegels - 1}
              >
                <Meta delen={["Gelezen, maar niet gebruikt in het antwoord"]} />
                {t.ontwikkelingen.map((o) => (
                  <OverwogenRegel
                    key={o.id}
                    o={o}
                    actief={oplicht(o.nr)}
                    onKlik={onOntwikkeling ? () => onOntwikkeling(o.id, o.titel) : undefined}
                  />
                ))}
              </TrendRegel>
            ))}
          </Lijst>
        </div>
      )}
      {metNu.length > 0 && <WaaromNuStrip trends={metNu} />}
      {nietIngedeeld.length > 0 && (
        <div data-testid="niet-ingedeeld" style={{ fontSize: 12, color: color.muted, lineHeight: 1.5, marginTop: 8 }}>
          Daarnaast{" "}
          {meervoud(nietIngedeeld.length, "bevinding die nog bij geen trend hoort", "bevindingen die nog bij geen trend horen")}.
        </div>
      )}
    </div>
  );
}

/** CH-A "17 bronnen · overheid en praktijk": het aantal bronnen met de twee
 *  soorten die de trend het vaakst draagt (trendlaag_trends.bron_soorten telt
 *  de ontwikkelingen per soort), in dezelfde woorden als de metaregel van de
 *  Trends-tab (woorden.ts, soortenVan). Zonder soorten alleen het aantal. */
export function bronSoortTekst(bronnen: number | null, soorten?: Record<string, number> | null): string | null {
  const top = Object.entries(soorten ?? {})
    .filter(([s, n]) => s !== "onbekend" && n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 2)
    .map(([s]) => s);
  const aantal = typeof bronnen === "number" ? meervoud(bronnen, "bron", "bronnen") : null;
  return [aantal, top.length ? top.join(" en ") : null].filter(Boolean).join(" · ") || null;
}

/** Eén regel per trend in de vorm van LijstRij (ChatBlokken): stip, naam, de
 *  labels (alleen "Wet" en beweging, CH-A), de nummers als chipjes, bronnen,
 *  chevron. Klik klapt de ontwikkelingen uit. */
function TrendRegel({
  titel,
  nummers,
  bronnen,
  opkomend,
  wet,
  beweging: bewegingLabel,
  overwogen,
  open,
  onWissel,
  laatste,
  children,
}: {
  titel: string;
  nummers: number[];
  bronnen: number | null;
  opkomend?: boolean;
  wet?: boolean;
  beweging?: string | null;
  overwogen?: boolean;
  open: boolean;
  onWissel: () => void;
  laatste: boolean;
  children: ReactNode;
}) {
  const [hover, setHover] = useState(false);
  const bew = overwogen ? null : beweging(bewegingLabel ?? null);
  return (
    <div
      data-testid="rust-trend"
      data-overwogen={overwogen ? "1" : undefined}
      style={{ borderBottom: laatste ? "none" : `1px solid ${color.lineFaint}` }}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={onWissel}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 11px", width: "100%",
          background: hover ? color.surfaceAlt : "none", border: "none", padding: "9px 14px", font: "inherit",
          fontSize: 13, textAlign: "left", cursor: "pointer", transition: "background 0.12s",
        }}
      >
        {overwogen ? (
          <span style={{ width: 7, height: 7, borderRadius: radius.pill, background: color.disabled, flexShrink: 0 }} />
        ) : (
          <Stip soort="trend" />
        )}
        <span
          style={{
            flex: "1 1 180px", minWidth: 0, fontWeight: weight.medium, lineHeight: 1.4,
            color: overwogen ? color.muted : color.ink,
          }}
        >
          {titel}
          {opkomend ? <span style={{ fontWeight: weight.regular, color: color.faint }}> · opkomend</span> : null}
        </span>
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {overwogen && <Pil tekst="overwogen" />}
          {wet && !overwogen && <Pil tekst="Wet" toon="blauw" />}
          {bew && <Pil tekst={bew.woord} toon={bew.toon} />}
          <span style={{ display: "inline-flex", gap: 3 }}>
            {nummers.map((n) => (
              <Nummer key={n} nr={n} uit={overwogen} />
            ))}
          </span>
          {typeof bronnen === "number" && <KV label="bronnen" waarde={String(bronnen)} />}
          <span
            aria-hidden
            style={{
              color: hover ? color.ink : color.disabled, fontWeight: weight.medium, display: "inline-block",
              transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s",
            }}
          >
            ›
          </span>
        </span>
      </button>
      {open && <div style={{ paddingBottom: 2 }}>{children}</div>}
    </div>
  );
}

function Meta({ delen }: { delen: (string | null | undefined)[] }) {
  const tekst = delen.filter(Boolean).join(" · ");
  if (!tekst) return null;
  return <div style={{ padding: "0 14px 7px 32px", fontSize: 11.5, color: color.soft }}>{tekst}</div>;
}

/** De eerste regel onder een opengeklapte trend: onderwerp en bronnen, en de
 *  afslag naar de trendkaart (klik op de trend = de trend; klik op een
 *  ontwikkeling = die ontwikkeling). */
function TrendVoet({ delen, onOpen }: { delen: (string | null | undefined)[]; onOpen?: () => void }) {
  const tekst = delen.filter(Boolean).join(" · ");
  if (!onOpen) return <Meta delen={delen} />;
  return (
    <button
      type="button"
      data-testid="rust-trend-open"
      onClick={onOpen}
      style={{
        display: "flex", alignItems: "baseline", gap: 10, width: "100%", border: "none", background: "none",
        padding: "0 14px 7px 32px", font: "inherit", fontSize: 11.5, color: color.soft, textAlign: "left", cursor: "pointer",
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{tekst}</span>
      <span style={{ flexShrink: 0, fontSize: 12, fontWeight: weight.medium, color: color.ink, whiteSpace: "nowrap" }}>
        Open trend →
      </span>
    </button>
  );
}

/** Hoe lang een regel oplicht na een klik op zijn nummer in de tekst. */
const OPLICHTEN_MS = 1600;

function useOplichten(keer: number | null) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [aan, setAan] = useState(false);
  useEffect(() => {
    if (keer === null) return;
    setAan(true);
    ref.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    const t = window.setTimeout(() => setAan(false), OPLICHTEN_MS);
    return () => window.clearTimeout(t);
  }, [keer]);
  return { ref, aan };
}

const OPLICHT_ACHTERGROND = `color-mix(in srgb, ${color.accentPurple} 10%, transparent)`;

/** Een ontwikkeling: nummer, titel · datum, de afzenders als pil, de werkzin
 *  klein eronder; klik = de ontwikkelingenkaart in het paneel (CH-A). */
function OntwikkelingRegel({ o, actief, onKlik }: { o: RustOntwikkeling; actief: number | null; onKlik?: () => void }) {
  const { ref, aan } = useOplichten(actief);
  const datum = datumRegel(o);
  const namen = o.bronNamen ?? [];
  const getoond = namen.slice(0, 2);
  const meer = Math.max((typeof o.bronnen === "number" ? o.bronnen : namen.length) - getoond.length, 0);
  return (
    <div ref={ref} data-testid={`rust-ontwikkeling-${o.nr}`} data-actief={aan ? "1" : undefined}>
      <button
        type="button"
        onClick={onKlik}
        style={{
          display: "flex", gap: 10, alignItems: "flex-start", width: "100%", border: "none",
          borderTop: `1px solid ${color.lineFaint}`, padding: "8px 14px", font: "inherit", fontSize: 13,
          textAlign: "left", cursor: onKlik ? "pointer" : "default", transition: "background 0.3s",
          background: aan ? OPLICHT_ACHTERGROND : "none",
        }}
      >
        <Nummer nr={o.nr} />
        <span style={{ flex: 1, minWidth: 0, lineHeight: 1.5 }}>
          <span style={{ fontWeight: weight.medium, color: color.ink }}>{o.titel}</span>
          {datum && <span style={{ color: color.soft }}> · {datum}</span>}
          {o.opkomend && <span style={{ color: color.soft }}> · opkomend</span>}
          {getoond.length > 0 ? (
            <BronPil namen={getoond} meer={meer} />
          ) : (
            typeof o.bronnen === "number" && <span style={{ color: color.soft }}> · {meervoud(o.bronnen, "bron", "bronnen")}</span>
          )}
          {o.werkzin && <span style={{ display: "block", fontSize: 12, color: color.soft, marginTop: 1 }}>{o.werkzin}</span>}
        </span>
        {onKlik && <span style={{ color: color.disabled, fontWeight: weight.medium }}>→</span>}
      </button>
    </div>
  );
}

/** Een ontwikkeling die de tools vonden maar het antwoord niet gebruikte: grijs,
 *  met de reden uit de data (service: "niet genoemd in het antwoord · …");
 *  klik = ook hier de ontwikkelingenkaart. */
function OverwogenRegel({ o, actief, onKlik }: { o: Overwogen; actief: number | null; onKlik?: () => void }) {
  const { ref, aan } = useOplichten(actief);
  return (
    <div ref={ref} data-testid={`rust-overwogen-${o.nr}`}>
      <button
        type="button"
        onClick={onKlik}
        style={{
          display: "flex", gap: 10, alignItems: "flex-start", width: "100%", border: "none", padding: "8px 14px",
          font: "inherit", fontSize: 13, textAlign: "left", cursor: onKlik ? "pointer" : "default",
          borderTop: `1px solid ${color.lineFaint}`, transition: "background 0.3s",
          background: aan ? OPLICHT_ACHTERGROND : "none",
        }}
      >
        <Nummer nr={o.nr} uit />
        <span style={{ flex: 1, minWidth: 0, lineHeight: 1.5, color: color.muted }}>
          {o.titel}
          <span style={{ display: "block", fontSize: 12, color: color.soft }}>{o.reden || "niet genoemd in het antwoord"}</span>
        </span>
        {onKlik && <span style={{ color: color.disabled, fontWeight: weight.medium }}>→</span>}
      </button>
    </div>
  );
}

/** CH-A: één aparte strip ná de trends, per trend een amberkaartje met het getal
 *  groot, de zin, de trend en de afzenders. */
function WaaromNuStrip({ trends }: { trends: RustTrend[] }) {
  return (
    <div style={{ marginTop: 12 }}>
      <Bloklabel tekst="Waarom nu" />
      <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))" }}>
        {trends.map((t) => {
          const strip = waaromNuStrip(t.waaromNu, t.waaromNuGetal);
          if (!strip) return null;
          const namen = t.waaromNuBronnen ?? [];
          return (
            <div
              key={t.trendId}
              data-testid="waarom-nu"
              style={{ background: color.accentAmberBg, borderRadius: radius.card, padding: "12px 14px", minWidth: 0 }}
            >
              {strip.getal && (
                <b
                  style={{
                    display: "block", fontSize: 22, fontWeight: weight.heavy, color: color.ink, lineHeight: 1.15,
                    fontVariantNumeric: "tabular-nums", marginBottom: 4,
                  }}
                >
                  {strip.getal}
                </b>
              )}
              <div style={{ fontSize: 12.5, color: color.body, lineHeight: 1.5 }}>{strip.zin}</div>
              <div style={{ fontSize: 11.5, color: color.soft, marginTop: 6, lineHeight: 1.7 }}>
                {t.titel}
                {namen.length > 0 && <BronPil namen={namen.slice(0, 2)} meer={Math.max(namen.length - 2, 0)} />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** De afzenders als één grijze pil: "ecp.nl · ncsc.nl +2" (CH-A). */
function BronPil({ namen, meer }: { namen: string[]; meer: number }) {
  return (
    <span
      data-testid="bronpil"
      style={{
        display: "inline-block", marginLeft: 6, padding: "0 7px", fontSize: 11, lineHeight: "18px", whiteSpace: "nowrap",
        color: color.muted, background: color.surfaceAlt, border: `1px solid ${color.lineSoft}`, borderRadius: 5,
        verticalAlign: "1px", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis",
      }}
    >
      {namen.join(" · ")}
      {meer > 0 ? ` +${meer}` : ""}
    </span>
  );
}

/** De pil-kleur per toon (woorden.ts: groeit = groen, minder recent bewijs =
 *  oranje; Wet = blauw); platformtokens, dus licht en donker. */
const PIL_KLEUR: Record<string, string> = {
  groen: color.accentGreen, oranje: color.accentAmber, blauw: color.accentBlue, rood: color.accentRed,
};

function Pil({ tekst, toon }: { tekst: string; toon?: string }) {
  const k = toon ? PIL_KLEUR[toon] : undefined;
  return (
    <span
      data-testid="rust-pil"
      style={{
        fontSize: 10.5, fontWeight: weight.bold, borderRadius: radius.pill, padding: "2px 8px", whiteSpace: "nowrap",
        background: k ? `color-mix(in srgb, ${k} 13%, transparent)` : color.lineFaint,
        color: k ?? color.muted,
      }}
    >
      {tekst}
    </span>
  );
}

/** Het nummer van een ontwikkeling: hetzelfde als [n] in de antwoordtekst. */
export function Nummer({ nr, uit }: { nr: number; uit?: boolean }) {
  return (
    <span
      aria-label={`ontwikkeling ${nr}`}
      style={{
        flex: "0 0 auto", minWidth: 18, height: 18, padding: "0 4px", boxSizing: "border-box", borderRadius: 4,
        fontSize: 11, fontWeight: weight.bold, display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontVariantNumeric: "tabular-nums", marginTop: 1,
        color: uit ? color.faint : color.accentPurple,
        background: uit ? color.lineFaint : `color-mix(in srgb, ${color.accentPurple} 12%, transparent)`,
      }}
    >
      {nr}
    </span>
  );
}

/** De telkaart bij aan-stand: het aantal trends groot (daar vraagt men naar),
 *  de ontwikkelingen en bevindingen in de zin ernaast. Zelfde vorm als TelKaart (ChatBlokken); de afslag naar de Trends-tab alleen
 *  zonder filter, want alleen dan toont die tab hetzelfde getal. */
export function TrendlaagTelKaart({
  telling,
  onOpen,
}: {
  telling: NonNullable<Kader["tellingen"]>[number];
  onOpen?: (landing: KaartLanding) => void;
}) {
  const filter = ((telling as { filter?: Record<string, unknown> }).filter ?? {}) as Record<string, unknown>;
  const zonderFilter = Object.keys(filter).length === 0;
  const filterTekst = Object.entries(filter)
    .map(([k, v]) => (v === true ? k : `${k} ${String(v)}`))
    .join(" · ");
  return (
    <div
      data-testid="trendlaag-telkaart"
      style={{ border: `1px solid ${color.line}`, borderRadius: radius.card, background: color.surface, padding: "13px 16px", margin: "4px 0 12px", maxWidth: 420 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: weight.bold, letterSpacing: "0.08em", textTransform: "uppercase", color: color.faint, marginBottom: 6 }}>
        <span style={{ width: 7, height: 7, borderRadius: radius.pill, background: color.accentBlue }} />
        Telling · trendlaag
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 32, fontWeight: weight.heavy, color: color.ink, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
          {nf.format(telling.trends)}
        </span>
        <span style={{ fontSize: 13, color: color.body }}>
          {telling.trends === 1 ? "trend" : "trends"}, met {nf.format(telling.ontwikkelingen)}{" "}
          {telling.ontwikkelingen === 1 ? "ontwikkeling" : "ontwikkelingen"} en {nf.format(telling.bevindingen)}{" "}
          {telling.bevindingen === 1 ? "bevinding" : "bevindingen"}
        </span>
      </div>
      <div
        style={{
          display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${color.lineFaint}`,
          marginTop: 10, paddingTop: 8, fontSize: 11.5, color: color.soft, gap: 10,
        }}
      >
        <span>{zonderFilter ? "hele trendlaag" : filterTekst}</span>
        {zonderFilter && onOpen ? (
          <button
            type="button"
            onClick={() => onOpen({ soort: "tab", tab: "trends" })}
            style={{ background: "none", border: "none", padding: 0, font: "inherit", fontSize: 12, fontWeight: weight.medium, color: color.ink, cursor: "pointer", whiteSpace: "nowrap" }}
          >
            Bekijk de trends →
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** Het bonnetje bij aan-stand (CH-A "weegt 5 trends · kiest 3"): hoeveel trends
 *  de tools in de trendlaag tegenkwamen (gebruikt plus overwogen) en hoeveel
 *  het antwoord er koos ("Waar dit op rust"). Alleen getallen uit het kader;
 *  zonder kader (een ouder gesprek) nul en nul, dan zegt het bonnetje er niets
 *  over. */
export function trendlaagWeging(kader: Kader | null | undefined): { gewogen: number; gekozen: number } {
  if (!kader) return { gewogen: 0, gekozen: 0 };
  const ids = new Set(kader.rust.trends.map((t) => t.trendId));
  for (const o of kader.rust.overwogen) ids.add(o.trendId ?? `titel:${o.trendTitel ?? ""}`);
  return { gewogen: ids.size, gekozen: kader.rust.trends.length };
}
